import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  ProductNotificationGroupBy,
  ProductNotificationStatus,
  type BackInStockProduct,
} from "@redotech/redo-customer-common/customer-product-notification/customer-product-notification";
import EmailIcon from "@redotech/redo-icons/mail-02.svg";
import UploadIcon from "@redotech/redo-icons/upload-01_filled.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoTableLoadingStateContent } from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { getDateEndOfDay } from "@redotech/util/date";
import { memo, useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { ProductDetailView } from "./bis/product-detail-view";
import { ProductListItem } from "./bis/product-list-item";
import { SearchAndFilterBar } from "./bis/search-filter-bar";
import { UploadBisModal } from "./upload-bis-modal";

const GROUP_BY_OPTIONS: { value: ProductNotificationGroupBy; el: string }[] = [
  { value: ProductNotificationGroupBy.PRODUCT, el: "Products" },
  { value: ProductNotificationGroupBy.VARIANT, el: "Variants" },
];

export const MarketingAnalyticsBackInStock = memo(
  function MarketingAnalyticsBackInStock() {
    const [groupBy, setGroupBy] = useState(ProductNotificationGroupBy.PRODUCT);
    const [searchQuery, setSearchQuery] = useState("");
    const [subscriberSearch, setSubscriberSearch] = useState("");
    const debouncedSearch = useDebounce(searchQuery, 500);
    const [selectedProductId, setSelectedProductId] = useState<string | null>(
      null,
    );
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    const [dateFilter, setDateFilter] = useState<
      [Temporal.PlainDate, Temporal.PlainDate] | undefined
    >();
    const [channelFilters, setChannelFilters] = useState<
      MarketingChannel[] | undefined
    >();
    const [statusFilters, setStatusFilters] = useState<
      ProductNotificationStatus[] | undefined
    >();

    const {
      data: productsData,
      isLoading,
      refetch,
    } = useRpcQuery(RedoMarketingRpcClientContext, {
      method: "getBackInStockProducts",
      args: {
        limit: 50,
        searchQuery: debouncedSearch || undefined,
        groupBy,
        dateFrom: dateFilter ? new Date(dateFilter[0].toString()) : undefined,
        dateTo: dateFilter
          ? getDateEndOfDay(new Date(dateFilter[1].toString()))
          : undefined,
        channels: channelFilters,
        statuses: statusFilters,
      },
      options: { placeholderData: (prev) => prev },
    });

    const products: BackInStockProduct[] = useMemo(
      () => productsData?.products || [],
      [productsData?.products],
    );
    const selectedProduct =
      products.find((p) => p.id === selectedProductId) || products[0];

    useEffect(() => {
      if (!selectedProductId && products.length > 0) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setSelectedProductId(products[0].id);
      }
    }, [products, selectedProductId]);

    const handleGroupByChange = (newGroupBy: ProductNotificationGroupBy) => {
      setGroupBy(newGroupBy);
      setSelectedProductId(null);
    };

    return (
      <Flex dir="row" gap="none" height="full" w="full">
        <Flex
          bgColor="primary"
          borderBottomLeftRadius="xl"
          borderColor="primary"
          borderRightWidth="1px"
          borderStyle="solid"
          borderTopLeftRadius="xl"
          dir="column"
          flex={1}
          gap="none"
          overflow="hidden"
        >
          <Flex dir="column" gap="md" p="lg">
            <SearchAndFilterBar
              channelFilters={channelFilters}
              dateFilter={dateFilter}
              placeholder="Search products..."
              searchQuery={searchQuery}
              setChannelFilters={setChannelFilters}
              setDateFilter={setDateFilter}
              setSearchQuery={setSearchQuery}
              setStatusFilters={setStatusFilters}
              statusFilters={statusFilters}
              trailingButtons={
                <RedoButton
                  aria-label="Upload"
                  hierarchy="secondary"
                  IconLeading={UploadIcon}
                  onClick={() => setUploadModalOpen(true)}
                  size="sm"
                />
              }
            />
            <RedoButtonTabGroup
              onChange={handleGroupByChange}
              options={GROUP_BY_OPTIONS}
              size="sm"
              value={groupBy}
            />
          </Flex>

          <Divider />

          {isLoading ? (
            <Flex align="center" height="quarter-screen" justify="center">
              <RedoTableLoadingStateContent />
            </Flex>
          ) : products.length === 0 ? (
            <RedoEmptyState
              Icon={EmailIcon}
              subtext="Products with waiting subscribers will appear here."
              text="No back in stock products"
            />
          ) : (
            <Flex dir="column" flex={1} gap="sm" overflow="auto" p="lg">
              {products.map((product) => (
                <ProductListItem
                  isSelected={product.id === selectedProductId}
                  key={product.id}
                  onClick={() => setSelectedProductId(product.id)}
                  product={product}
                />
              ))}
            </Flex>
          )}
        </Flex>

        <Flex
          bgColor="primary"
          borderBottomRightRadius="xl"
          borderTopRightRadius="xl"
          flex={3}
          overflow="hidden"
        >
          {selectedProduct ? (
            <ProductDetailView
              channelFilters={channelFilters}
              dateFilter={dateFilter}
              product={selectedProduct}
              refetchAllProducts={refetch}
              setChannelFilters={setChannelFilters}
              setDateFilter={setDateFilter}
              setStatusFilters={setStatusFilters}
              setSubscriberSearch={setSubscriberSearch}
              statusFilters={statusFilters}
              subscriberSearch={subscriberSearch}
            />
          ) : (
            <Flex align="center" height="full" justify="center" w="full">
              <RedoEmptyState
                Icon={EmailIcon}
                subtext="Choose a product from the list to view details."
                text="Select a product"
              />
            </Flex>
          )}
        </Flex>

        <UploadBisModal
          onClose={() => setUploadModalOpen(false)}
          onUploadComplete={() => refetch()}
          open={uploadModalOpen}
        />
      </Flex>
    );
  },
);
